import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetGenericEvent} from 'features/core/shared/widget/event/widget-generic-event';
import {
    MvsWidgetFormGenericEventConstants
} from 'features/core/shared/widget/component/runtime/implementations/mvs-widget-form/mvs-widget-form-generic-event.constants';
import {MvsFormGroup} from 'features/core/shared/logic/form/mvs-form-group';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {
    CpProvisionAgentStatementService
} from 'features/feature-prov/cp/service/api/cp-provision-agent-statement.service';
import {CpFeatureRight} from 'features/feature-prov/cp/service/domain/cp-feature-right';
import {ProvisionAgentStatementDto} from 'features/feature-prov/cp/model/dto/entity/provision-agent-statement.dto';
import {CpAgentStatementResult} from 'features/feature-prov/cp/interface/cp-agent-statement-result.interface';
import {cpServerMessage} from 'features/feature-prov/cp/service/domain/cp-server-message';

/**
 * Action dialog "Abrechnung neu ausstellen" — `POST /mvsa/cp/provisionAgentStatements/{id}/reissue`
 * (cp-agent-statement GR-S3, right `cp_agentStatement`).
 *
 * A FINAL statement is immutable; a correction is only possible as a NEW version with a NEW number
 * and a MANDATORY reason, the old version becomes SUPERSEDED and stays in dm. This dialog captures
 * that reason (max. 500 characters), shows which statement (number / version) is replaced, calls
 * {@link CpProvisionAgentStatementService#reissue$} and emits the result (`statementId`, `statementNumber`,
 * `version`); the HOST refreshes the list. The global error toast is suppressed for the call — the
 * dialog shows the server's German sentence (`error.data`) inline and as toast.
 *
 * FORM MECHANISM: the same TRANSIENT FORM WIDGET as `cp-provision-agent-manual-booking-dialog`
 * (MvsFormDto built in the frontend -> `WidgetFactory.createWidget(..., 'form', 'transient', ...)`
 * -> `<mvs-widget>`). The field is rendered and validated by the framework (`uiMandatory`,
 * `uiStringMaxLength` -> German framework messages); this component holds NO hand-written form
 * HTML. The write goes to the custom endpoint, never to the generic CRUD (which the backend refuses
 * for statements).
 *
 * RIGHTS: optimistic + 403 demotion like every cp action ({@link CpFeatureRight}). A 403 shows the
 * German refusal and fires {@link onForbidden} so the host can lock its entry point.
 */
@Component({
    selector: 'cp-provision-agent-statement-reissue-dialog',
    templateUrl: './cp-provision-agent-statement-reissue-dialog.component.html',
    styleUrls: ['./cp-provision-agent-statement-reissue-dialog.component.scss'],
    standalone: false
})
export class CpProvisionAgentStatementReissueDialogComponent implements OnInit, OnChanges, OnDestroy {

    /** Maximum length of the reissue reason (mirrors the backend column). */
    static readonly REASON_MAX_LENGTH: number = 500;

    busy: boolean;
    initialized: boolean;

    /** The ACTIVE statement to be replaced. */
    @Input() statement: ProvisionAgentStatementDto;

    /** Visibility of the dialog; intended as a two-way binding (`[(visible)]`). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** Fires with the server result (new statement id / number / version) after a successful reissue. */
    @Output() reissued = new EventEmitter<CpAgentStatementResult>();

    /** Fires when the server refused the right `cp_agentStatement` with a 403. */
    @Output() onForbidden = new EventEmitter<void>();

    /** The transient capture widget (see class doc). */
    formWidget: WidgetData;

    /** Current reason as typed (also while the form is still invalid — drives the counter). */
    reasonValue: string = '';

    /** True when the framework form reports VALID. */
    formValid: boolean = false;

    /** Inline error shown in the dialog after a failed reissue. */
    failureMessage: string = null;

    readonly reasonMaxLength: number = CpProvisionAgentStatementReissueDialogComponent.REASON_MAX_LENGTH;

    constructor(
        protected statementService: CpProvisionAgentStatementService,
        protected messageService: MvsMessageService,
        protected mvsObjectService: MvsObjectService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.initialized = true;
        if (this.visible) {
            this.refreshComponent();
        }
    }

    /** Rebuilds the form on every open, so no stale reason survives. */
    refreshComponent(): void {
        this.reasonValue = '';
        this.formValid = false;
        this.failureMessage = null;
        this.formWidget = null;

        if (!this.statement?.id) {
            return;
        }

        this.buildFormWidget();
    }

    /** Builds the one-field MvsFormDto (reason) and the transient form widget from it. */
    private buildFormWidget(): void {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        const reasonField = MvsFormFieldDto.createBasic('reason', 'textarea', 'Grund der Neuausstellung');
        reasonField.uiMandatory = true;
        reasonField.uiStringMaxLength = this.reasonMaxLength;
        reasonField.uiDescription = `Pflichtangabe, höchstens ${this.reasonMaxLength} Zeichen. `
            + 'Der Grund wird an der ersetzten Fassung gespeichert.';
        reasonField.order = 10;
        formFields['reason'] = reasonField;

        form.formFields = formFields;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Transient form widget for the mandatory reason of a reissue of a final
         *         Provisionsabrechnung (cp.ProvisionAgentStatement) — MvsFormDto built in the frontend
         *         (no entity template); the write goes to POST /cp/provisionAgentStatements/{id}/reissue
         *         and replaces the statement by a new version with a new number.
         */
        this.formWidget = WidgetFactory.createWidget(
            'cp.agent.statement.reissue.form',
            'Abrechnung neu ausstellen',
            'form',
            'transient',
            'transient',
            'cp.ProvisionAgentStatement',
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('form', form)
        );
    }

    /** Receives the form value; validity comes from the framework form group. */
    handleGenericEvent(event: WidgetGenericEvent): void {

        if (event.eventName !== MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE) {
            return;
        }

        const formGroup: MvsFormGroup =
            event.object[MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE_ATTR_FORM_GROUP];

        const value = formGroup?.value?.['reason'];
        this.reasonValue = typeof value === 'string' ? value : '';
        this.formValid = formGroup?.status === 'VALID';
        this.failureMessage = null;
    }

    /** Length of the reason as typed. */
    get reasonLength(): number {
        return this.reasonValue?.length ?? 0;
    }

    /** True when the reason exceeds the maximum length. */
    get reasonTooLong(): boolean {
        return this.reasonLength > this.reasonMaxLength;
    }

    /** True when something was typed but only whitespace — `required` alone would accept it. */
    get reasonBlank(): boolean {
        return this.reasonLength > 0 && this.reasonValue.trim().length === 0;
    }

    /** German validation hint shown below the form; null when the reason is fine or untouched. */
    get validationMessage(): string {
        if (this.reasonTooLong) {
            return `Der Grund darf höchstens ${this.reasonMaxLength} Zeichen lang sein.`;
        }
        if (this.reasonBlank) {
            return 'Bitte geben Sie einen Grund für die Neuausstellung an.';
        }
        return null;
    }

    /** The action button is only active with a valid, non-blank reason. */
    get submittable(): boolean {
        return this.formValid
            && !this.busy
            && !this.reasonTooLong
            && this.reasonValue.trim().length > 0
            && !!this.statement?.id;
    }

    /** Calls the reissue endpoint and emits the new statement. */
    handleReissue(): void {

        if (!this.submittable) {
            return;
        }

        this.busy = true;
        this.failureMessage = null;

        this.statementService.reissue$(this.statement.id, this.reasonValue.trim()).subscribe({
            next: (outcome: CpAgentStatementResult) => {
                this.busy = false;

                this.messageService.showSuccessMessage('Abrechnung neu ausgestellt',
                    outcome?.message
                        || (outcome?.statementNumber
                            ? `Die neue Fassung ${outcome.statementNumber} wurde erstellt.`
                            : 'Die neue Fassung wurde erstellt.'));

                // module-wide change signal for other listeners; the statement list itself is re-read
                // ONCE, by the host on `reissued` (it does not also react to this event)
                this.mvsObjectService.objectChangeEvent.next(
                    ObjectChangeInformation.create(
                        'cp.ProvisionAgentStatement',
                        ObjectChangeInformationActionEnum.created,
                        this.statement,
                        this.reissuedStatementOf(outcome)));

                this.reissued.emit(outcome);
                this.handleClose();
            },
            error: (error) => {
                this.busy = false;

                if (CpFeatureRight.isForbidden(error)) {
                    this.failureMessage = CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_STATEMENT);
                    this.messageService.showErrorMessage(CpFeatureRight.DENIED_HEADER, this.failureMessage);
                    this.onForbidden.emit();
                    return;
                }

                cpServerMessage(error)
                    .catch(() => null)
                    .then((serverMessage: string | null) => {
                        this.failureMessage = serverMessage
                            ?? 'Die Abrechnung konnte nicht neu ausgestellt werden. Bitte versuchen Sie es erneut.';
                        this.messageService.showErrorMessage('Neuausstellung fehlgeschlagen', this.failureMessage);
                    });
            }
        });
    }

    /** The slim DTO of the new version for the object-change event (the list re-reads the full row). */
    private reissuedStatementOf(outcome: CpAgentStatementResult): ProvisionAgentStatementDto | null {
        if (!outcome?.statementId) {
            return null;
        }
        const created = new ProvisionAgentStatementDto();
        created.id = outcome.statementId;
        created.statementNumber = outcome.statementNumber;
        created.version = outcome.version;
        return created;
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if ((changes['visible'] || changes['statement']) && this.visible) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

}
